import { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';
import { useDebouncedSave } from 'hooks/useDebounceSave';

import { fillPeriodValueWithInitialValue } from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';

import { EQUITY_NAMES, useGetEquityQuestionList } from './utils/form';
import { useUnitChange } from '../../hooks/useUnitChange';
import {
  EQUITY_TABLE_NAMES,
  LIABILITIES_AND_EQUITY_FORM_NAMES,
} from '../../ProjectedBalanceSheets/Sections/LiabilitiesAndEquity/utils/form';
import { liabilitiesAndEquityPrefix } from '../../ProjectedBalanceSheets/utils/form';
import { QuestionsForm } from '../components';
import { DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS, initialEquityFormPrefix } from '../utils/form';

export const InitialEquityForm: FC = () => {
  const { t } = useTranslation();

  const { getValues, setValue } = useFormContext();
  const equityQuestionList = useGetEquityQuestionList()

  const { isLoading } = useUnitChange();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.OPENING_BALANCE_SHEETS,
  );

  const [reserves, accumulatedOtherComprehensiveIncome, additionalPaidInCapital] = getValues([
    concatWithDot(initialEquityFormPrefix, EQUITY_NAMES.RESERVES),
    concatWithDot(initialEquityFormPrefix, EQUITY_NAMES.ACCUMULATED_OTHER_COMPREHENSIVE_INCOME),
    concatWithDot(initialEquityFormPrefix, EQUITY_NAMES.ADDITIONAL_PAID_IN_CAPITAL),
  ]);

  const [reservesFieldName, accumulatedOtherComprehensiveIncomeFieldName, additionalPaidInCapitalFieldName] = [
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.EQUITY_TABLE,
      EQUITY_TABLE_NAMES.RESERVES,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.EQUITY_TABLE,
      EQUITY_TABLE_NAMES.ACCUMULATED_OTHER_COMPREHENSIVE_INCOME,
    ),
    concatWithDot(
      liabilitiesAndEquityPrefix,
      LIABILITIES_AND_EQUITY_FORM_NAMES.EQUITY_TABLE,
      EQUITY_TABLE_NAMES.ADDITIONAL_PAID_IN_CAPITAL,
    ),
  ];

  const reservesHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(reservesFieldName, periodValue);
  }, []);

  const accumulatedOtherComprehensiveIncomeHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(accumulatedOtherComprehensiveIncomeFieldName, periodValue);
  }, []);

  const additionalPaidInCapitalHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(additionalPaidInCapitalFieldName, periodValue);
  }, []);

  useDebouncedSave({
    data: reserves,
    saveFunction: reservesHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: accumulatedOtherComprehensiveIncome,
    saveFunction: accumulatedOtherComprehensiveIncomeHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  
  useDebouncedSave({
    data: additionalPaidInCapital,
    saveFunction: additionalPaidInCapitalHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });

  return (
    <>
      <QuestionsForm
        questionList={equityQuestionList}
        disabled={!canAccess}
        name={initialEquityFormPrefix}
        title={t('financialPlan.openingBalanceSheets.initialEquity.title')}
        collapsible
        isLoading={isLoading}
      />
    </>
  );
};
